<script setup>
import { reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, FormField, Input, Modal, Textarea } from '@drama/ui'

const props = defineProps({
  open: { type: Boolean, required: true },
  type: { type: String, required: true },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  filters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['update:open', 'submit'])
const { t } = useI18n()

const form = reactive({
  email: '',
  display_name: '',
  locale: 'en',
  use_case: '',
  note: '',
  marketing_consent_accepted_at: '',
  invite_note: '',
  reason: '',
})

watch(
  () => props.open,
  (open) => {
    if (!open) resetForm()
  },
)

function resetForm() {
  form.email = ''
  form.display_name = ''
  form.locale = 'en'
  form.use_case = ''
  form.note = ''
  form.marketing_consent_accepted_at = ''
  form.invite_note = ''
  form.reason = ''
}

function title() {
  switch (props.type) {
    case 'manual':
      return t('visualGameAdmin.waitlist.modal.manualTitle')
    case 'invite':
      return t('visualGameAdmin.waitlist.modal.inviteTitle')
    case 'resend':
      return t('visualGameAdmin.waitlist.modal.resendTitle')
    case 'note':
      return t('visualGameAdmin.waitlist.modal.noteTitle')
    case 'reject':
      return t('visualGameAdmin.waitlist.modal.rejectTitle')
    case 'export':
      return t('visualGameAdmin.waitlist.modal.exportTitle')
    default:
      throw new Error(`Unhandled modal type: ${props.type}, id: visual-game-admin`)
  }
}

function submit() {
  switch (props.type) {
    case 'manual':
      emit('submit', {
        email: form.email,
        display_name: form.display_name,
        locale: form.locale,
        source: 'manual_admin',
        use_case: form.use_case,
        note: form.note,
        marketing_consent_accepted_at: form.marketing_consent_accepted_at,
      })
      return
    case 'invite':
    case 'resend':
      emit('submit', { note: form.invite_note })
      return
    case 'note':
      emit('submit', { note: form.note })
      return
    case 'reject':
      emit('submit', { reason: form.reason })
      return
    case 'export':
      emit('submit', props.filters)
      return
    default:
      throw new Error(`Unhandled modal type: ${props.type}, id: visual-game-admin`)
  }
}
</script>

<template>
  <Modal
    :open="open"
    :title="title()"
    @update:open="emit('update:open', $event)"
  >
    <form
      class="action-modal"
      data-insp-path="apps/visual-game-admin/src/components/WaitlistActionModal.vue#modal"
      @submit.prevent="submit"
    >
      <template v-if="type === 'manual'">
        <FormField :label="t('visualGameAdmin.auth.email')" required>
          <Input v-model="form.email" type="email" autocomplete="off" required />
        </FormField>
        <FormField :label="t('visualGameAdmin.waitlist.fields.displayName')">
          <Input v-model="form.display_name" autocomplete="off" />
        </FormField>
        <FormField :label="t('visualGameAdmin.waitlist.fields.locale')" required>
          <Input v-model="form.locale" autocomplete="off" required />
        </FormField>
        <FormField :label="t('visualGameAdmin.waitlist.fields.useCase')" required>
          <Input v-model="form.use_case" autocomplete="off" required />
        </FormField>
        <FormField :label="t('visualGameAdmin.waitlist.fields.consentAcceptedAt')" required>
          <Input v-model="form.marketing_consent_accepted_at" type="datetime-local" required />
        </FormField>
        <FormField :label="t('visualGameAdmin.waitlist.fields.note')">
          <Textarea v-model="form.note" :maxlength="2000" />
        </FormField>
      </template>

      <FormField
        v-if="type === 'invite' || type === 'resend'"
        :label="t('visualGameAdmin.waitlist.modal.operatorNote')"
      >
        <Textarea v-model="form.invite_note" :maxlength="2000" />
      </FormField>

      <FormField v-if="type === 'note'" :label="t('visualGameAdmin.waitlist.fields.note')" required>
        <Textarea v-model="form.note" :maxlength="2000" required />
      </FormField>

      <FormField v-if="type === 'reject'" :label="t('visualGameAdmin.waitlist.modal.rejectReason')" required>
        <Textarea v-model="form.reason" :maxlength="1000" required />
      </FormField>

      <p v-if="type === 'export'" class="vga-muted">
        {{ t('visualGameAdmin.waitlist.modal.exportSummary') }}
      </p>
      <p v-if="error" class="vga-error">{{ error }}</p>
    </form>

    <template #footer>
      <Button variant="ghost" :disabled="loading" @click="emit('update:open', false)">
        {{ t('visualGameAdmin.common.cancel') }}
      </Button>
      <Button :variant="type === 'reject' ? 'danger' : 'primary'" :loading="loading" @click="submit">
        {{ type === 'export' ? t('visualGameAdmin.common.export') : t('visualGameAdmin.common.confirm') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.action-modal {
  display: grid;
  gap: var(--drama-space-lg);
}
</style>
